// A-OrderBlocks — mechanical Fair Value Gap + Order Block detection from
// OHLCV candles. Zero AI, zero guessing — same discipline as every other
// A_ agent in this folder.
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 UNLIKE A_Structure/A_Fibonacci/A_Regime, THIS IS NOT BACKTESTED.
// Those three were ported from JarvisSMC_Strategy.pine after real Pine
// backtesting (30+ trades/combo). This module is new, purely mechanical
// ICT-concept detection that has never been validated the same way.
//
// It exists ONLY to auto-populate the ICT/SMC Module and Confluence Engine
// where Order Blocks/FVGs were previously 100% manual entry — it must
// NEVER feed into A_Signal.js's actual BUY/SELL decision. Consuming code
// should treat this as reference/informational, exactly like the manual
// entries it replaces, not as a validated trade condition.
// ═══════════════════════════════════════════════════════════════════════════

// ── Fair Value Gap — pure 3-candle mechanical pattern ───────────────────────
// Bullish FVG: candle[i-2].high < candle[i].low (gap left behind by the
// impulsive middle candle). Bearish: candle[i-2].low > candle[i].high.
// Tracks whether later price action has traded back into the gap
// ("filled") — an unfilled FVG is the ICT-relevant one.
function detectFVGs(candles, maxLookback = 200) {
  const start = Math.max(2, candles.length - maxLookback);
  const fvgs = [];

  for (let i = start; i < candles.length; i++) {
    const c1 = candles[i - 2];
    const c3 = candles[i];

    if (c1.high < c3.low) {
      fvgs.push({ type: 'bullish', top: c3.low, bottom: c1.high, index: i, date: candles[i - 1].date, filled: false });
    } else if (c1.low > c3.high) {
      fvgs.push({ type: 'bearish', top: c1.low, bottom: c3.high, index: i, date: candles[i - 1].date, filled: false });
    }
  }

  // Mark filled: any LATER candle trading back into the gap's range.
  for (const gap of fvgs) {
    for (let j = gap.index + 1; j < candles.length; j++) {
      const c = candles[j];
      if (c.low <= gap.top && c.high >= gap.bottom) { gap.filled = true; gap.filledAt = j; break; }
    }
  }

  return fvgs;
}

// ── Order Block — last opposite-colour candle before the impulsive leg
// that led to a confirmed swing pivot. Takes the swing pivot's index
// directly from A_Structure's output, so it's anchored to the SAME
// confirmed-pivot definition already used everywhere else — not a
// separate, inconsistent pivot detector.
function findOrderBlock(candles, pivotIndex, wantBullish, maxLookback = 20) {
  if (pivotIndex == null) return null;
  const floor = Math.max(0, pivotIndex - maxLookback);
  for (let i = pivotIndex; i >= floor; i--) {
    const c = candles[i];
    const isDownClose = c.close < c.open;
    const isUpClose = c.close > c.open;
    // A bullish OB is the last DOWN-close candle before an up-move; a
    // bearish OB is the last UP-close candle before a down-move.
    if (wantBullish && isDownClose) {
      return { type: 'bullish', high: c.high, low: c.low, index: i, date: c.date };
    }
    if (!wantBullish && isUpClose) {
      return { type: 'bearish', high: c.high, low: c.low, index: i, date: c.date };
    }
  }
  return null;
}

// candles: same OHLCV array A_Structure/A_Fibonacci use.
// structure: A_Structure.run()'s output — reused so the Order Block below
// is anchored to the exact same pivot the rest of the app is already
// looking at, not a second, possibly-disagreeing pivot detector.
export async function run({ candles = [], structure = null }) {
  if (!candles || candles.length < 5) {
    return { ok: false, error: 'Need at least 5 candles', agent: 'A-OrderBlocks', requiresAI: false, ts: Date.now() };
  }

  const fvgs = detectFVGs(candles);
  const unfilledFvgs = fvgs.filter((f) => !f.filled);

  let currentOrderBlock = null;
  if (structure?.valid) {
    const isBull = structure.trend === 'BULLISH';
    const pivotIndex = isBull ? structure.swingHigh?.index : structure.swingLow?.index;
    currentOrderBlock = findOrderBlock(candles, pivotIndex, isBull);
  }

  return {
    ok: true,
    fvgs,
    unfilledFvgs,
    currentOrderBlock,
    validated: false, // never let this get confused with the backtested agents
    note: 'Mechanically detected (FVG/OB), never backtested — reference only, does not feed the BUY/SELL signal.',
    agent: 'A-OrderBlocks',
    requiresAI: false,
    ts: Date.now(),
  };
}
